import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Upload, FileText, PlusCircle, Sparkles, LayoutTemplate, Zap,
  CheckCircle, ArrowRight, Target, Clock, Shield, Star,
  TrendingUp, Users, Award, Layers
} from "lucide-react";

const STATS = [
  { value: "300k+", label: "Resumes Built",       icon: Users },
  { value: "3×",    label: "Faster to Complete",  icon: Clock },
  { value: "50+",   label: "Pro Templates",        icon: LayoutTemplate },
  { value: "98%",   label: "ATS Pass Rate",        icon: Shield },
];

const FEATURES = [
  {
    icon: Sparkles,
    title: "AI-Powered Writing",
    desc: "Describe what you did — AI turns it into recruiter-ready bullet points with strong action verbs and quantified results.",
  },
  {
    icon: Target,
    title: "ATS Optimization",
    desc: "Every resume is scored and optimized to pass Applicant Tracking Systems before it ever reaches a human.",
  },
  {
    icon: LayoutTemplate,
    title: "Professional Templates",
    desc: "50+ templates designed by hiring managers at FAANG companies. Clean, modern, and proven to get callbacks.",
  },
  {
    icon: Zap,
    title: "Real-Time Feedback",
    desc: "Instant suggestions as you type — catch weak phrasing, missing keywords, and formatting issues on the fly.",
  },
  {
    icon: TrendingUp,
    title: "Performance Scoring",
    desc: "See your resume score across five dimensions: impact, clarity, keywords, completeness, and design.",
  },
  {
    icon: Award,
    title: "Industry-Specific Insights",
    desc: "Tailored guidance for tech, finance, consulting, product, and 20+ other career tracks.",
  },
];

const WHY_BULLETS = [
  "Smart keyword optimization",
  "ATS compatibility",
  "Industry-specific insights for professional designs",
  "Industry-optimized mobile responsive live preview",
  "Instant feedback",
  "Performance scoring",
];

const PROMO = [
  { icon: Users,         stat: "300,000+", line: "Resumes built and counting" },
  { icon: Clock,         stat: "3× faster", line: "Time optimization vs. building manually" },
  { icon: LayoutTemplate,stat: "50+",      line: "Professional templates across every industry" },
];

export default function ResumeBuilder() {
  const nav = useNavigate();

  return (
    <div className="min-h-screen">

      {/* ── Hero ── */}
      <div className="px-10 pt-12 pb-10 border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20 pointer-events-none" />
        <div className="relative max-w-4xl">
          <div className="inline-flex items-center gap-2 border border-white/10 px-3 py-1.5 mb-6 text-[11px] uppercase tracking-widest text-neutral-300">
            <span className="w-1.5 h-1.5 bg-[#D3FF24] rounded-full" />
            AI Resume Builder — Powered by Wingman AI
          </div>
          <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter leading-[0.95] mb-5">
            Build a resume that<br />
            <span className="text-[#D3FF24]" style={{ textShadow: "0 0 40px rgba(211,255,36,0.3)" }}>
              actually gets interviews.
            </span>
          </h1>
          <p className="text-neutral-400 text-lg max-w-xl leading-relaxed mb-8">
            AI writes, optimizes, and scores your resume in minutes. ATS-ready from the start. Used by 300,000+ job seekers at top companies.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => nav("/app/resume/new")}
              className="group inline-flex items-center gap-2 bg-[#D3FF24] text-black px-6 py-3.5 font-semibold uppercase tracking-widest text-xs hover:brightness-105 transition"
            >
              Build from scratch <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
            </button>
            <button
              onClick={() => nav("/app/resume/import")}
              className="inline-flex items-center gap-2 border border-white/15 px-6 py-3.5 text-xs font-semibold uppercase tracking-widest hover:bg-white/5 transition"
            >
              <Upload className="w-4 h-4" /> Import existing resume
            </button>
          </div>
        </div>
      </div>

      {/* ── Stats bar ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border-b border-white/10">
        {STATS.map((s) => (
          <div key={s.label} className="bg-[#0A0A0A] px-8 py-6 flex items-center gap-4">
            <s.icon className="w-5 h-5 text-[#D3FF24] shrink-0" strokeWidth={1.75} />
            <div>
              <div className="font-display text-2xl font-black tracking-tight text-white">{s.value}</div>
              <div className="text-xs text-neutral-500 mt-0.5">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="px-10 py-14 max-w-5xl">

        {/* ── Action cards ── */}
        <div className="mb-16">
          <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-3">/ Get started</div>
          <h2 className="font-display text-3xl font-bold tracking-tight mb-8">How would you like to begin?</h2>

          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            <button
              onClick={() => nav("/app/resume/import")}
              className="bg-[#0A0A0A] hover:bg-[#0F0F0F] p-8 text-left transition group flex flex-col"
            >
              <div className="w-10 h-10 border border-white/10 flex items-center justify-center mb-5 group-hover:border-[#D3FF24]/40 transition">
                <Upload className="w-5 h-5 text-[#D3FF24]" strokeWidth={1.75} />
              </div>
              <h3 className="font-display text-lg font-semibold mb-2 group-hover:text-[#D3FF24] transition">Import Existing</h3>
              <p className="text-sm text-neutral-500 leading-relaxed flex-1 mb-5">
                Upload your current resume. AI analyzes, restructures, and enhances it for modern hiring.
              </p>
              <span className="text-xs uppercase tracking-widest font-semibold text-[#D3FF24] flex items-center gap-1">
                Upload & enhance <ArrowRight className="w-3 h-3" />
              </span>
            </button>

            <button className="bg-[#0A0A0A] p-8 text-left flex flex-col relative cursor-not-allowed opacity-50">
              <div className="absolute top-4 right-4 text-[10px] uppercase tracking-widest text-neutral-600 border border-white/10 px-2 py-0.5">
                Coming soon
              </div>
              <div className="w-10 h-10 border border-white/10 flex items-center justify-center mb-5">
                <FileText className="w-5 h-5 text-neutral-600" strokeWidth={1.75} />
              </div>
              <h3 className="font-display text-lg font-semibold mb-2 text-neutral-500">Continue Editing</h3>
              <p className="text-sm text-neutral-600 leading-relaxed flex-1 mb-5">
                Pick up where you left off. Your draft is auto-saved and ready whenever you are.
              </p>
              <span className="text-xs uppercase tracking-widest font-semibold text-neutral-600 flex items-center gap-1">
                Continue editing <ArrowRight className="w-3 h-3" />
              </span>
            </button>

            <button
              onClick={() => nav("/app/resume/new")}
              className="bg-[#0A0A0A] hover:bg-[#0F0F0F] p-8 text-left transition group flex flex-col"
            >
              <div className="w-10 h-10 border border-white/10 flex items-center justify-center mb-5 group-hover:border-[#D3FF24]/40 transition">
                <PlusCircle className="w-5 h-5 text-[#D3FF24]" strokeWidth={1.75} />
              </div>
              <h3 className="font-display text-lg font-semibold mb-2 group-hover:text-[#D3FF24] transition">Build from Scratch</h3>
              <p className="text-sm text-neutral-500 leading-relaxed flex-1 mb-5">
                Guided 5-step builder. Fill in your details and AI generates a polished, interview-ready resume.
              </p>
              <span className="text-xs uppercase tracking-widest font-semibold text-[#D3FF24] flex items-center gap-1">
                Start building <ArrowRight className="w-3 h-3" />
              </span>
            </button>
          </div>
        </div>

        {/* ── Feature grid ── */}
        <div className="mb-16">
          <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-3">/ Features</div>
          <h2 className="font-display text-3xl font-bold tracking-tight mb-8">Everything your resume needs</h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-[#0A0A0A] hover:bg-[#0E0E0E] p-7 transition group">
                <f.icon className="w-5 h-5 text-[#D3FF24] mb-4" strokeWidth={1.75} />
                <h3 className="font-display font-semibold text-base mb-2 group-hover:text-[#D3FF24] transition">{f.title}</h3>
                <p className="text-sm text-neutral-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Promotional stats ── */}
        <div className="mb-16 border border-white/10 bg-[#0A0A0A]">
          <div className="px-8 pt-8 pb-6 border-b border-white/10">
            <h2 className="font-display text-2xl font-bold tracking-tight">
              Trusted by job seekers who get hired.
            </h2>
            <p className="text-sm text-neutral-500 mt-1">Real numbers. Real results.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-white/10">
            {PROMO.map((p) => (
              <div key={p.stat} className="bg-[#0A0A0A] px-8 py-8 flex flex-col gap-3">
                <p.icon className="w-6 h-6 text-[#D3FF24]" strokeWidth={1.5} />
                <div className="font-display text-4xl font-black tracking-tight text-[#D3FF24]"
                  style={{ textShadow: "0 0 30px rgba(211,255,36,0.25)" }}>
                  {p.stat}
                </div>
                <p className="text-sm text-neutral-400 leading-snug">{p.line}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Why choose / bullets ── */}
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mb-3">/ Why choose us</div>
          <h2 className="font-display text-3xl font-bold tracking-tight mb-8">Why Choose Our Resume Builder</h2>

          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-6">
            {[
              { icon: Sparkles,      label: "AI-Powered Intelligence",  desc: "Writes, rewrites, and optimizes every line for you." },
              { icon: LayoutTemplate,label: "Professional Templates",   desc: "Designed by hiring managers, tested by recruiters." },
              { icon: Zap,           label: "Real-Time Optimization",   desc: "Instant scoring and feedback as you build." },
            ].map((t) => (
              <div key={t.label} className="bg-[#0A0A0A] p-6">
                <t.icon className="w-5 h-5 text-[#D3FF24] mb-3" strokeWidth={1.75} />
                <div className="font-display font-semibold text-sm text-white mb-1">{t.label}</div>
                <div className="text-xs text-neutral-500">{t.desc}</div>
              </div>
            ))}
          </div>

          <div className="border border-white/10 p-8 bg-[#0A0A0A]">
            <ul className="grid sm:grid-cols-2 gap-3">
              {WHY_BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-neutral-300">
                  <CheckCircle className="w-4 h-4 text-[#D3FF24] shrink-0 mt-0.5" strokeWidth={1.75} />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
}
